import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import axios from "axios";

const defaultDraft = {
  id: null,
  name: "",
  sortOrder: "",
};

const toOrderValue = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export default function ClientStatusesTab({ instanceUniqueId }) {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(defaultDraft);
  const [instanceId, setInstanceId] = useState(null);

  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
  const headers = { headers: { accessToken: localStorage.getItem("accessToken") } };

  const fetchInstanceId = async () => {
    if (!instanceUniqueId) return;
    const res = await axios.get(
      `${SERVER}/instances/byinstanceUniqueId/${instanceUniqueId}`,
      headers
    );
    setInstanceId(res.data?.id || null);
  };

  const fetchStatuses = async (targetInstanceId = instanceId) => {
    if (!targetInstanceId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${SERVER}/instances/${targetInstanceId}/client-statuses`,
        headers
      );
      setStatuses(res.data.statuses || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInstanceId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceUniqueId]);

  useEffect(() => {
    if (instanceId) fetchStatuses(instanceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId]);

  const openCreate = () => {
    setDraft(defaultDraft);
    setOpen(true);
  };

  const openEdit = (status) => {
    setDraft({
      id: status.id,
      name: status.name || "",
      sortOrder: status.sortOrder ?? "",
    });
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      name: draft.name.trim(),
      sortOrder: toOrderValue(draft.sortOrder),
    };
    if (!payload.name || !instanceId) return;

    if (draft.id) {
      await axios.put(
        `${SERVER}/instances/${instanceId}/client-statuses/${draft.id}`,
        payload,
        headers
      );
    } else {
      await axios.post(
        `${SERVER}/instances/${instanceId}/client-statuses`,
        payload,
        headers
      );
    }

    setOpen(false);
    fetchStatuses(instanceId);
  };

  const remove = async (status) => {
    if (!instanceId) return;
    await axios.delete(
      `${SERVER}/instances/${instanceId}/client-statuses/${status.id}`,
      headers
    );
    fetchStatuses(instanceId);
  };

  const isValid = useMemo(() => {
    if (!draft.name.trim()) return false;
    return draft.sortOrder === "" || Number.isFinite(Number(draft.sortOrder));
  }, [draft]);

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Client Statuses
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Manage client statuses and their display order.
          </Typography>
        </Box>

        <Button variant="contained" onClick={openCreate}>
          Add Client Status
        </Button>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Client Status</TableCell>
            <TableCell>Sort Order</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(statuses || []).map((status) => (
            <TableRow key={status.id} hover>
              <TableCell sx={{ fontWeight: 700 }}>{status.name}</TableCell>
              <TableCell>{status.sortOrder ?? "—"}</TableCell>
              <TableCell align="right">
                <IconButton onClick={() => openEdit(status)} size="small">
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton onClick={() => remove(status)} size="small">
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {!loading && (statuses || []).length === 0 && (
            <TableRow>
              <TableCell colSpan={3} sx={{ py: 4, textAlign: "center", opacity: 0.7 }}>
                No client statuses yet. Add your first one.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {draft.id ? "Edit Client Status" : "Create Client Status"}
        </DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: 1 }}>
          <TextField
            label="Client Status"
            value={draft.name}
            onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
          />
          <TextField
            label="Sort Order"
            type="number"
            value={draft.sortOrder}
            onChange={(e) => setDraft((prev) => ({ ...prev, sortOrder: e.target.value }))}
            helperText="Optional. Lower numbers appear first."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!isValid}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
